import { useEffect, useState } from "react";
import { Menu, MenuItem, ProSidebar } from "react-pro-sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import { tokens } from "../../theme";
import "react-pro-sidebar/dist/css/styles.css";

const Item = ({ title, to, icon, selected, onNavigate }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    onNavigate(title);
    navigate(to);
  };

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: selected === title ? "#9c27b0" : "white",
        transition: "all 0.3s ease-in-out",
        cursor: "pointer",
      }}
      onClick={handleClick}
      icon={icon}
    >
      <Typography
        className="text-lg font-bold text-center"
        style={{
          fontWeight: selected === title ? "800" : "600",
          transform: selected === title ? "scale(1.1)" : "scale(1)",
          transition: "all 0.3s ease-in-out",
        }}
      >
        {title}
      </Typography>
    </MenuItem>
  );
};

const NAV_LINKS = [
  { title: "Dashboard", to: "/dd", Icon: DashboardOutlinedIcon },
  { title: "Seller", to: "/seller", Icon: StorefrontOutlinedIcon },
  { title: "Renter", to: "/renter", Icon: PeopleOutlineIcon },
  { title: "Vehicle", to: "/vehicle", Icon: DirectionsCarOutlinedIcon },
  { title: "Bookings", to: "/bookings", Icon: EventNoteOutlinedIcon },
  { title: "Payment", to: "/payment", Icon: PaymentOutlinedIcon },
  { title: "User Stripe Account", to: "/userstripe", Icon: AccountBalanceWalletOutlinedIcon },
  { title: "Contact Customer", to: "/contact", Icon: ContactMailOutlinedIcon },
];

const routeToTitle = (pathname) => {
  const match = NAV_LINKS.find(({ to }) => {
    if (to === "/dd") return pathname === "/dd";
    return pathname === to || pathname.startsWith(`${to}/`);
  });
  return match?.title ?? "Dashboard";
};

const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState(() => routeToTitle(location.pathname));

  useEffect(() => {
    setSelected(routeToTitle(location.pathname));
  }, [location.pathname]);

  const getIconColor = (title) => (selected === title ? "#9c27b0" : "white");

  const handleNav = (title) => {
    setSelected(title);
    if (isMobile) onMobileClose();
  };

  const navItems = NAV_LINKS.map(({ title, to, Icon }) => (
    <Item
      key={title}
      title={title}
      to={to}
      icon={<Icon style={{ color: getIconColor(title) }} />}
      selected={selected}
      onNavigate={handleNav}
    />
  ));

  const sidebarSx = {
    "& .pro-sidebar-inner": {
      background: "linear-gradient(to top, #1a237e, #42a5f5) !important",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)",
      position: "relative",
      overflow: "hidden",
    },
    "& .pro-icon-wrapper": {
      backgroundColor: "transparent !important",
    },
    "& .pro-inner-item": {
      padding: "7px 35px 14px 20px !important",
      margin: "6px 0",
      transition: "all 0.25s ease-in-out !important",
      textAlign: "center",
      position: "relative",
      zIndex: 5,
      cursor: "pointer",
    },
    "& .pro-menu-item": {
      cursor: "pointer",
    },
    "& .pro-inner-item:hover": {
      color: "#9c27b0 !important",
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      fontWeight: "bold",
    },
    "& .pro-inner-item:hover .pro-item-content": {
      fontWeight: "700 !important",
    },
    "& .pro-menu-item.active": {
      color: "#9c27b0 !important",
      backgroundColor: "rgba(255, 255, 255, 0.15) !important",
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
      transform: "scale(1.02)",
    },
    "& .pro-menu-item svg": {
      fontSize: "1.5rem",
      marginRight: "10px",
      transition: "all 0.3s ease",
    },
    "& .pro-menu-item:hover svg, & .pro-menu-item.active svg": {
      transform: "scale(1.15)",
      color: "#9c27b0 !important",
    },
    "& .pro-item-content": {
      textAlign: "center",
      width: "100%",
      transition: "all 0.25s ease-in-out",
      letterSpacing: "0.3px",
    },
  };

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 280,
            background: "linear-gradient(to top, #1a237e, #42a5f5)",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h5" fontWeight="bold" letterSpacing={1}>
            Oi-Jo Admin
          </Typography>
        </Box>
        <Box sx={{ px: 1, pb: 2, ...sidebarSx }}>
          <Menu iconShape="square">{navItems}</Menu>
        </Box>
      </Drawer>
    );
  }

  return (
    <Box sx={sidebarSx}>
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "24px 0",
              color: colors.grey[100],
              position: "relative",
              zIndex: 5,
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
                position="relative"
                sx={{ transform: "translateY(-5px)" }}
              >
                <Typography
                  variant="h2"
                  style={{
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    fontFamily: "'Poppins', sans-serif",
                    position: "relative",
                    zIndex: 3,
                    color: "white",
                    textShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  ADMIN
                </Typography>
                <IconButton
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  style={{
                    color: "white",
                    zIndex: 3,
                    position: "relative",
                  }}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box
            height="85.5vh"
            paddingLeft={isCollapsed ? 0 : "10%"}
            position="relative"
          >
            {navItems}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
